/**
 * Device helpers. Chrome/Android only (v3 section 9).
 */
import { useEffect } from 'react'

/** One position fix, or null after `timeoutMs`. Never throws; a missing fix
 *  must never block a stop. */
export function getPosition(timeoutMs = 8000): Promise<{ lat: number; lng: number; accuracy: number } | null> {
  return new Promise((resolve) => {
    const geo = globalThis.navigator?.geolocation
    if (!geo) return resolve(null)
    let done = false
    const finish = (v: { lat: number; lng: number; accuracy: number } | null) => {
      if (!done) {
        done = true
        resolve(v)
      }
    }
    const timer = setTimeout(() => finish(null), timeoutMs)
    geo.getCurrentPosition(
      (pos) => {
        clearTimeout(timer)
        finish({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy })
      },
      () => {
        clearTimeout(timer)
        finish(null)
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 15000 },
    )
  })
}

/** Keep the screen on while a stop is open. Re-acquired when the page is
 *  shown again (Chrome releases the lock on hide). */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active) return
    let sentinel: { release: () => Promise<void> } | null = null
    let cancelled = false
    const acquire = async () => {
      try {
        const wl = (navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } }).wakeLock
        if (!wl) return
        sentinel = await wl.request('screen')
      } catch {
        /* denied or unsupported: the driver's screen timeout applies */
      }
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !cancelled) void acquire()
    }
    void acquire()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisible)
      void sentinel?.release()
    }
  }, [active])
}

/** Google Maps deep link for one leg. Opens the Maps app on Android. */
export function navigateUrl(stop: { latitude: number | null; longitude: number | null; address: string; city: string; postal_code: string }): string {
  const dest =
    stop.latitude != null && stop.longitude != null
      ? `${stop.latitude},${stop.longitude}`
      : encodeURIComponent(`${stop.address}, ${stop.city} ${stop.postal_code}`)
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`
}
